import { useEffect, useRef } from "react"
import { useNavigation, useRoute } from "@react-navigation/native"
import { useInscriptionFunnel } from "../context/InscriptionFunnelContext"
import { useInscriptionLead } from "./useInscriptionLead"
import {
    ensureUserInUrl,
    isOtherLead,
    readUserParam,
    userNavParams,
} from "@common/utils/funnelLinkIdentity"

// Les écrans du funnel les importent d'ici.
export {
    clearUserFromUrl,
    readUserParam,
    userNavParams,
} from "@common/utils/funnelLinkIdentity"

/**
 * Un funnel sans DOSSIER doit repasser par la reprise — c'est l'identifiant qui
 * décide, pas la présence de réponses.
 *
 * La garde exigeait auparavant que TOUT soit vide. Or l'état qui casse est
 * précisément l'état partiel : `studentClass` survit au sessionStorage pendant
 * que `funnelStateId` a disparu (il n'entre dans le Context que par un callback
 * asynchrone, et le repli Redux est remis à zéro à la réhydratation). Le
 * contexte passait donc pour chaud, la reprise ne se déclenchait jamais, et le
 * parcours continuait hors-sol jusqu'à échouer au configurateur.
 *
 * Le `paid` reste une sortie : un parcours payé n'a plus rien à reprendre.
 *
 * L'identifiant se lit par {@link useInscriptionLead} — Context PUIS store — et
 * une création en vol suspend la décision : pendant l'aller-retour de `start`,
 * il n'y a ni identifiant ni échec, et conclure « pas de dossier » renverrait au
 * début exactement le chemin nominal (classe pré-remplie, clic en moins d'une
 * seconde).
 */
const needsResume = (funnelStateId, isResolvingLead, state) =>
    !funnelStateId && !isResolvingLead && !state?.paid

/**
 * Garde unique partagée par tous les écrans du funnel Inscription (refresh-safe +
 * partageable par URL) — clone de useStageFunnelGuard, sans le param
 * `template` qui n'a pas d'équivalent ici (ce funnel vend l'offre régulière,
 * il n'y a pas de catalogue à résoudre) :
 *
 *  1. Propage `?user=` dans l'URL web de chaque étape.
 *  2. Pas de dossier AVEC `user` → ré-hydrate via `inscription/resume?user=…`,
 *     qui l'ouvre s'il n'existe pas encore.
 *  3. Pas de dossier SANS `user` → retour au début du funnel (jamais d'écran
 *     blanc).
 *  4. `user` DIFFÉRENT du lead en mémoire → même reprise, même avec un dossier
 *     en mémoire : l'URL fait autorité sur ce qui traîne dans l'onglet.
 *
 * `isStart` exempte l'écran de départ de la redirection « retour au début » ;
 * il déclenche quand même la reprise quand un `user` est présent à froid.
 */
export const useInscriptionFunnelGuard = ({ isStart = false } = {}) => {
    const navigation = useNavigation()
    const route = useRoute()
    const { state } = useInscriptionFunnel()
    // Le jeton d'identité du lead, connu dès l'entrée. Il sert de REPLI quand
    // l'URL n'en porte pas : c'est le cas de la porte directe depuis la
    // landing, où le parent n'a jamais de `?user=`.
    const {
        userToken: leadToken,
        funnelStateId,
        isResolvingLead,
    } = useInscriptionLead()
    // Mémorise le jeton déjà arbitré, et non un simple booléen : un changement
    // de lead dans le même onglet doit pouvoir redéclencher la décision.
    const handledRef = useRef(null)

    // L'URL fait foi ; à défaut on écrit celui du lead connu. Sans ça, une
    // entrée directe déroulait tout le parcours sur des URLs nues : un refresh
    // ou un lien copié à mi-chemin repartait au début, alors que l'été et le
    // stage sont reprenables à chaque étape.
    const userParam = readUserParam(route) || leadToken

    useEffect(() => {
        if (userParam) ensureUserInUrl(userParam)
    }, [userParam])

    useEffect(() => {
        const otherLead = isOtherLead(state, userParam)

        // La reprise a DÉJÀ statué sur ce jeton (`resumedToken`, écrit dans le
        // même patch que l'hydratation) : un dossier absent est une réponse du
        // serveur, pas un contexte froid. Sans ce court-circuit, un lead sans
        // dossier rebondissait start → resume → start à l'infini (le
        // `handledRef` renaît à chaque remontage), jusqu'au crash React #185 —
        // le cas de la bannière de reprise des familles été. Un jeton
        // DIFFÉRENT (changement de lead) repasse bien par la reprise.
        if (
            state?.resumedToken &&
            state.resumedToken === userParam &&
            !otherLead
        )
            return

        // Une seule décision de routage par jeton `user` traité.
        if (handledRef.current === (userParam ?? "")) return
        if (!needsResume(funnelStateId, isResolvingLead, state) && !otherLead)
            return

        handledRef.current = userParam ?? ""

        if (userParam) {
            navigation.reset({
                index: 0,
                routes: [
                    { name: "inscription/resume", params: { user: userParam } },
                ],
            })
            return
        }

        if (!isStart) {
            navigation.reset({
                index: 0,
                routes: [{ name: "inscription/start" }],
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, userParam, funnelStateId, isResolvingLead])

    return { userParam, nextParams: userNavParams(userParam) }
}
